/**
 * Canonical subscription tier representation (DB + backend).
 *
 * Marketing / UI may use aliases (e.g. pro_monthly). Always normalize before
 * comparing or persisting.
 */

export type CanonicalTier =
  | 'free'
  | 'day_pass'
  | 'monthly_pro'
  | 'annual_pro'
  | 'campus_monthly'
  | 'campus_annual'
  | 'district'
  /** Admin-granted complimentary access — never self-serve checkout */
  | 'educator_pilot';

/** @deprecated Use CanonicalTier — kept as alias for existing imports */
export type SubscriptionTier = CanonicalTier;

const LEGACY_ALIASES: Record<string, CanonicalTier> = {
  pro_monthly: 'monthly_pro',
  pro_annual: 'annual_pro',
};

const CANONICAL = new Set<string>([
  'free',
  'day_pass',
  'monthly_pro',
  'annual_pro',
  'campus_monthly',
  'campus_annual',
  'district',
  'educator_pilot',
]);

/** Display label for account status UI (not a pricing card). */
export function tierDisplayLabel(tier: CanonicalTier): string {
  if (tier === 'educator_pilot') return 'Educator / Faculty Pilot';
  if (tier === 'day_pass') return 'Day Pass';
  if (tier.includes('campus')) return 'Campus';
  if (tier === 'district') return 'District';
  if (tier === 'free') return 'Free';
  return 'Pro';
}

export function isEducatorPilotTier(tier: string | null | undefined): boolean {
  return normalizeTier(tier) === 'educator_pilot';
}

export function normalizeTier(value: string | null | undefined): CanonicalTier {
  if (!value) return 'free';
  if (value in LEGACY_ALIASES) return LEGACY_ALIASES[value];
  if (CANONICAL.has(value)) return value as CanonicalTier;
  return 'free';
}

/** Map pricing UI tier ids to checkout plan (canonical). */
export function checkoutPlanFromPricingTierId(
  tierId: string,
  billingPeriod: 'monthly' | 'annual'
): CanonicalTier | null {
  if (tierId === 'day_pass') return 'day_pass';
  if (tierId === 'pro_monthly' || tierId === 'monthly_pro') {
    return billingPeriod === 'annual' ? 'annual_pro' : 'monthly_pro';
  }
  if (tierId === 'pro_annual' || tierId === 'annual_pro') return 'annual_pro';
  // Campus/District: sales-assisted in Milestone 0
  return null;
}

export const RECURRING_PERSONAL_TIERS: CanonicalTier[] = ['monthly_pro', 'annual_pro'];

export const PAID_PERSONAL_CHECKOUT_TIERS: CanonicalTier[] = [
  'day_pass',
  'monthly_pro',
  'annual_pro',
];
